import type { IconName } from '@civilgrid/ui/components';
import {
  Badge,
  Button,
  Icon,
  NavMenu,
  NavMenuContent,
  NavMenuGroup,
  NavMenuItem,
  NavMenuTrigger,
} from '@civilgrid/ui/components';
import { cn } from '@civilgrid/ui/utils';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import authorizations, { Action } from '~/authorizations';
import { captureTypedPosthogEvent, POSTHOG_EVENTS, type UtilitiesExportsMenuItem } from '~/common/posthogEvents';
import { useQuery } from '~/common/routerUtils';
import { useExportLabels } from '~/hooks/useExportTerminologyEnabled';
import rootStore from '~/store';
import type { ProjectDetailsParams } from '../..';
import { EXPORT_ENTRY_POINT_PARAM, EXPORT_TAB_ID, UTILITIES_EXPORTS_MENU_ENTRY_POINT } from '../../exportTabIds';
import { EXPORTS_MENU_FOCUS_REPORTS, EXPORTS_MENU_SEARCH_PARAM, EXPORTS_MENU_SHOW_REPORTS } from '../common';
import { useReportExports } from '../reports/useReportExports';
import { markReportsMovedSeen } from '../reportsMovedSeen';
import store from '../store';
import { DownloadAllFilesModal, hasAgencyFiles } from '../Table/columns';
import { type ExportsMenuItem, getExportsMenuItems, NOT_IN_PLAN } from './exportsMenuItems';

const ITEM_ICONS: Record<UtilitiesExportsMenuItem, IconName> = {
  gis_cad_data: 'externalLink',
  outreach_log: 'download',
  records_research_report: 'envelopeClosed',
  communication_log: 'envelopeClosed',
  download_all_files: 'envelopeClosed',
};

/** Replaces the Reports subtab and the Download Data button when the Utilities page redesign is on. */
export default function ExportsMenu({
  hasDownloadsFeature,
  downloadsTurnedOff,
  hasReportsFeature,
  isFullUtilitiesExperience,
}: {
  hasDownloadsFeature: boolean;
  downloadsTurnedOff: boolean;
  hasReportsFeature: boolean;
  isFullUtilitiesExperience: boolean;
}) {
  const { projectId, tabId } = useParams({ strict: false }) as ProjectDetailsParams;
  const navigate = useNavigate();
  const query = useQuery();
  const labels = useExportLabels();
  const { me, myRole } = useSnapshot(rootStore) as typeof rootStore;
  const { isLoading, isError, project, agencies, dataRequests } = useSnapshot(store) as typeof store;
  // Report modals return focus here; the menu item that opened them is gone.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { hasActiveRequest, exportOutreachLog, openRecordsResearchReport, openCommunicationLog, modals } =
    useReportExports(projectId, 'exports_menu', triggerRef);

  const [open, setOpen] = useState(false);
  const [downloadAllOpen, setDownloadAllOpen] = useState(false);
  const [highlightReports, setHighlightReports] = useState(false);
  const [focusReports, setFocusReports] = useState(false);
  const firstReportRef = useRef<HTMLDivElement>(null);

  // `myRole` is read so the permission checks rerun when the role lands.
  const canCheck = (action: Action) => !!me && !!myRole && authorizations.can(action);
  const items = getExportsMenuItems({
    labels,
    hasDownloadsFeature,
    downloadsTurnedOff,
    hasReportsFeature,
    canDownloadData: canCheck(Action.DownloadData),
    canExportReports: canCheck(Action.ExportOutreachLog),
    roleName: myRole?.name,
    isFullUtilitiesExperience,
    hasActiveRequest,
    hasAgencyFiles: hasAgencyFiles(agencies, dataRequests),
    utilitiesLoaded: !isLoading && !isError && project?.id === projectId,
  });

  const exportsParam = query.get(EXPORTS_MENU_SEARCH_PARAM);
  const showReports = exportsParam === EXPORTS_MENU_SHOW_REPORTS || exportsParam === EXPORTS_MENU_FOCUS_REPORTS;
  useEffect(() => {
    if (!showReports) return;
    setOpen(true);
    setHighlightReports(true);
    setFocusReports(exportsParam === EXPORTS_MENU_FOCUS_REPORTS);
    // Drop the param so a refresh or Back doesn't reopen the menu.
    navigate({
      to: '/projects/$projectId/$tabId',
      params: { projectId, tabId },
      search: (prev: Record<string, unknown>) => ({ ...prev, [EXPORTS_MENU_SEARCH_PARAM]: undefined }),
      replace: true,
    });
  }, [showReports, exportsParam, navigate, projectId, tabId]);

  // Starts keyboard and screen reader users on the highlighted reports. Waits a frame for Radix's own open focus.
  useEffect(() => {
    if (!open || !focusReports) return;
    const frame = requestAnimationFrame(() => firstReportRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open, focusReports]);

  // The highlight stays until the user interacts with the menu or closes it.
  const clearHighlight = () => {
    if (highlightReports) setHighlightReports(false);
  };
  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setHighlightReports(false);
      setFocusReports(false);
    }
  };

  const select = (item: ExportsMenuItem) => {
    captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesExportsMenuItemSelected, { project_id: projectId, item: item.id });
    // Picking a report means they found where reports moved.
    if (item.group === 'reports') markReportsMovedSeen(me?.id);
    switch (item.id) {
      case 'gis_cad_data':
        // Its link does the navigating.
        break;
      case 'outreach_log':
        exportOutreachLog();
        break;
      case 'records_research_report':
        openRecordsResearchReport();
        break;
      case 'communication_log':
        openCommunicationLog();
        break;
      case 'download_all_files':
        setDownloadAllOpen(true);
        break;
    }
  };

  const renderItem = (item: ExportsMenuItem, index: number) => {
    const disabled = !!item.disabledReason;
    const content = (
      <>
        {/* Inherits the text color, so it darkens on hover. */}
        <Icon icon={ITEM_ICONS[item.id]} size={16} className={cn('mt-0.5', disabled && 'text-grey-40')} aria-hidden />
        <span className="flex min-w-0 flex-col">
          <span>{item.label}</span>
          <span className="font-normal text-grey-70">{item.disabledReason ?? item.hint}</span>
        </span>
      </>
    );
    const isLink = item.id === 'gis_cad_data' && !disabled;
    return (
      <NavMenuItem
        key={item.id}
        asChild={isLink}
        ref={item.group === 'reports' && index === 0 ? firstReportRef : undefined}
        aria-disabled={disabled || undefined}
        onSelect={(event) => {
          if (disabled) {
            event.preventDefault();
            return;
          }
          select(item);
        }}
        // A grey focus highlight: visible, but not the orange "pick me" one.
        className={cn(
          'items-start',
          disabled &&
            'cursor-not-allowed text-grey-50 hover:bg-transparent hover:text-grey-50 data-[highlighted]:bg-grey-20 data-[highlighted]:text-grey-50',
        )}
        data-testid={`utilities-exports-menu-${item.id.replaceAll('_', '-')}`}
      >
        {isLink ? (
          <Link
            to="/projects/$projectId/$tabId"
            params={{ projectId, tabId: EXPORT_TAB_ID }}
            search={{ [EXPORT_ENTRY_POINT_PARAM]: UTILITIES_EXPORTS_MENU_ENTRY_POINT }}
          >
            {content}
          </Link>
        ) : (
          content
        )}
      </NavMenuItem>
    );
  };

  const group = (id: ExportsMenuItem['group'], label: string, highlighted = false) => {
    const groupItems = items.filter((item) => item.group === id);
    if (groupItems.length === 0) return null;
    // One Upgrade badge per heading when the plan locks an item
    const needsUpgrade = groupItems.some((item) => item.disabledReason === NOT_IN_PLAN);
    return (
      <NavMenuGroup
        label={
          <span className={cn('flex items-center justify-between gap-2', highlighted && 'text-grey-90')}>
            {label}
            {needsUpgrade && (
              <Badge color="orange" size="small" data-testid={`utilities-exports-menu-upgrade-${id}`}>
                Upgrade
              </Badge>
            )}
          </span>
        }
        aria-label={needsUpgrade ? `${label}, Upgrade` : label}
        className={cn(
          'motion-safe:transition-[background-color,box-shadow] motion-safe:duration-500',
          highlighted && 'bg-orange-10 shadow-[inset_3px_0_0_var(--color-orange-50)]',
        )}
        data-highlighted={highlighted || undefined}
        data-testid={`utilities-exports-menu-group-${id}`}
      >
        {groupItems.map(renderItem)}
      </NavMenuGroup>
    );
  };

  return (
    <>
      <NavMenu open={open} onOpenChange={onOpenChange}>
        <NavMenuTrigger asChild>
          <Button ref={triggerRef} variant="secondary" data-testid="utilities-top-nav-exports">
            {labels.navItem}
            <Icon icon="chevronDown" size={14} aria-hidden />
          </Button>
        </NavMenuTrigger>
        <NavMenuContent
          align="end"
          className="w-[250px]"
          onPointerMove={clearHighlight}
          onKeyDown={clearHighlight}
          data-testid="utilities-exports-menu"
        >
          {group('data', 'Data')}
          {/* Marked after "Show me". */}
          {group('reports', 'Reports', highlightReports)}
        </NavMenuContent>
      </NavMenu>
      {modals}
      <DownloadAllFilesModal
        open={downloadAllOpen}
        onOpenChange={setDownloadAllOpen}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          triggerRef.current?.focus();
        }}
      />
    </>
  );
}
